import React, { useEffect, useState } from "react";
import { X, Send, Sparkles, Calculator, ShoppingCart } from "lucide-react";
import { useChatSession } from "@/hooks/useChatSession";
import { ChatMessages } from "@/components/chat/ChatMessages";
import { api } from "@/lib/api";

const FLO = {
  name: "Flo SunPress",
  avatar: "/img/flo-avatar-sm.webp",
  avatarLg: "/img/flo-avatar.webp",
};

const MODES = [
  { id: "qa", label: "Ask Flo a question", icon: Sparkles, intro: "Hi, I'm Flo 👋 — the face of SunPress Solar. Ask me anything about the CORE 500, the MistPro, or running appliances on solar and inverter power." },
  { id: "calculator", label: "Savings calculator", icon: Calculator, intro: "Let's find out how much the CORE 500 will save you. Roughly how many hours do you iron per week?" },
  { id: "sales", label: "Find the right product", icon: ShoppingCart, intro: "Tell me about your home — solar size, inverter, what you iron daily — and I'll recommend the perfect SunPress setup for you." },
];

const GreetingBubble = ({ onOpen, onDismiss }) => (
  <div data-testid="chat-greeting" className="chat-greet fixed z-50 bottom-40 right-4 md:right-auto md:bottom-24 md:left-8">
    <button data-testid="chat-greeting-close" onClick={onDismiss} aria-label="Dismiss chat greeting" className="chat-greet-close">
      <X size={13} />
    </button>
    <button data-testid="chat-greeting-open" onClick={onOpen} className="text-left flex items-start gap-3">
      <img
        src={FLO.avatar}
        alt="Flo SunPress"
        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
        style={{ border: "2px solid var(--sun-orange)" }}
      />
      <span>
        <span className="block text-sm font-medium" style={{ color: "var(--ink)" }}>
          Hi, I'm Flo 👋 Questions about the CORE 500?
        </span>
        <span className="block text-xs mt-1" style={{ color: "var(--ink-soft)" }}>
          Ask me anything — or estimate your savings.
        </span>
      </span>
    </button>
  </div>
);

const ModePicker = ({ onPick }) => (
  <div className="flex-1 p-5 flex flex-col gap-3 overflow-y-auto" style={{ background: "var(--bg)" }}>
    <p className="text-sm text-[color:var(--ink-soft)] mb-2">Hi, I'm Flo 👋 I can help three ways. Pick one:</p>
    {MODES.map((m) => {
      const Icon = m.icon;
      return (
        <button
          key={m.id}
          data-testid={`chat-mode-${m.id}`}
          onClick={() => onPick(m.id)}
          className="text-left p-4 hover:bg-white transition-colors rounded-xl"
          style={{ border: "1px solid var(--border)", background: "#fff", minHeight: 56 }}
        >
          <div className="flex items-center gap-3">
            <Icon size={20} color="var(--brand)" />
            <div className="font-medium text-sm">{m.label}</div>
          </div>
        </button>
      );
    })}
  </div>
);

const ChatHeader = ({ mode, onClose }) => (
  <div className="flex items-center justify-between p-4" style={{ background: "var(--ink)", color: "#fff" }}>
    <div className="flex items-center gap-3">
      <img
        src={FLO.avatarLg}
        alt="Flo SunPress"
        data-testid="chat-flo-avatar"
        className="w-10 h-10 rounded-full object-cover"
        style={{ border: "2px solid var(--sun-orange)" }}
      />
      <div>
        <div className="text-sm font-medium">{FLO.name}</div>
        <div className="text-xs opacity-70">
          {mode ? MODES.find((m) => m.id === mode)?.label : "Founder's twin · here to help"}
        </div>
      </div>
    </div>
    <button
      data-testid="chat-close"
      onClick={onClose}
      aria-label="Close chat"
      className="inline-flex items-center justify-center w-11 h-11 -mr-2"
    >
      <X size={18} />
    </button>
  </div>
);

const ChatInputBar = ({ input, setInput, sending, onSend, onSwitch }) => (
  <form
    onSubmit={onSend}
    className="p-3 flex gap-2 items-center"
    style={{ background: "#fff", borderTop: "1px solid var(--border)", paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}
  >
    <button
      type="button"
      data-testid="chat-switch-mode"
      onClick={onSwitch}
      className="text-xs text-[color:var(--ink-soft)] underline px-1 py-3"
    >
      Switch
    </button>
    <input
      data-testid="chat-input"
      className="input-field flex-1"
      placeholder="Type your message…"
      value={input}
      onChange={(e) => setInput(e.target.value)}
      disabled={sending}
    />
    <button data-testid="chat-send" aria-label="Send message" className="btn-brand" disabled={sending}>
      <Send size={16} />
    </button>
  </form>
);

const Launcher = ({ onOpen }) => (
  <button
    data-testid="chat-launcher"
    onClick={onOpen}
    aria-label="Chat with Flo — SunPress assistant"
    className="fixed z-50 bottom-24 right-4 md:right-auto md:bottom-8 md:left-8 inline-flex items-center justify-center gap-2.5 shadow-xl w-14 h-14 sm:w-auto sm:h-auto sm:pl-2 sm:pr-5 sm:py-2"
    style={{ background: "var(--ink)", color: "#fff", borderRadius: 999 }}
  >
    <img src={FLO.avatar} alt="" className="w-10 h-10 rounded-full object-cover" style={{ border: "2px solid var(--sun-orange)" }} />
    <span className="text-sm font-medium hidden sm:inline">Chat with Flo</span>
  </button>
);

const useChatVisibility = (startMode) => {
  const [open, setOpen] = useState(false);
  const [greet, setGreet] = useState(false);

  const dismissGreet = () => {
    setGreet(false);
    sessionStorage.setItem("sp_greet_seen", "1");
  };
  const openChat = () => { dismissGreet(); setOpen(true); };

  useEffect(() => {
    if (sessionStorage.getItem("sp_greet_seen")) return;
    const t = setTimeout(() => setGreet(true), 6000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      openChat();
      const m = e.detail?.mode;
      if (m && MODES.some((x) => x.id === m)) startMode(m);
    };
    window.addEventListener("sp:open-chat", handler);
    return () => window.removeEventListener("sp:open-chat", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { open, greet, openChat, dismissGreet, close: () => setOpen(false) };
};

const useProductCatalog = (enabled) => {
  const [products, setProducts] = useState(null);
  useEffect(() => {
    if (!enabled || products) return;
    api.get("/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, [enabled, products]);
  return products;
};

export default function ChatWidget() {
  const session = useChatSession(MODES);
  const { mode, messages, input, setInput, sending, startMode, resetMode, send } = session;
  const { open, greet, openChat, dismissGreet, close } = useChatVisibility(startMode);
  const products = useProductCatalog(open);

  return (
    <>
      {greet && !open && <GreetingBubble onOpen={openChat} onDismiss={dismissGreet} />}
      {!open && <Launcher onOpen={openChat} />}
      {open && (
        <div
          data-testid="chat-window"
          className="fixed z-50 inset-x-3 bottom-3 md:inset-x-auto md:bottom-8 md:left-8 md:w-[400px] flex flex-col shadow-2xl rounded-2xl overflow-hidden"
          style={{ background: "#fff", border: "1px solid var(--border)", height: "min(78dvh, 640px)" }}
          role="dialog"
          aria-label="SunPress Solar AI chat"
        >
          <ChatHeader mode={mode} onClose={close} />
          {!mode && <ModePicker onPick={startMode} />}
          {mode && (
            <>
              <ChatMessages messages={messages} sending={sending} avatar={FLO.avatar} products={products} />
              <ChatInputBar input={input} setInput={setInput} sending={sending} onSend={send} onSwitch={resetMode} />
            </>
          )}
        </div>
      )}
    </>
  );
}
